import { Injectable, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Dialog } from '@capacitor/dialog';
import { ModalController, ToastController } from '@ionic/angular/standalone';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { NewItemDialogComponent } from '../instructions/new/item/new-item.dialog';
import { News } from '../models/news.model';
import { PartnerNote } from '../models/partner-note.model';
import { Room } from '../models/room.model';
import { SiteArea } from '../models/site-area.model';
import { Site } from '../models/site.model';
import { User } from '../models/user.model';
import { NewNewsDialogComponent } from '../news/dialog/new-news.dialog';
import { PartnerNoteDialogComponent } from '../partner/notes/dialog/partner-note.dialog';
import { NewSiteDialogComponent } from '../sites/new-site/new-site.dialog';
import { NewSiteReportDialogComponent } from '../sites/site/components/reports/new/new-site-report.dialog';
import { AddAreaDialogComponent } from '../sites/site/components/rooms/components/add-area/add-area.dialog';
import { InviteUserDialogComponent } from '../users/invite/invite-user.dialog';
import { Roles } from '../utility/role';
import { NewMessageDialogComponent } from '../worker/messages/new-message/dialogs/new-message.dialog';
import { ClientsService } from './clients.service';
import { InitializeService } from './initialize.service';
import { NewsService } from './news.service';
import { PartnerNotesService } from './partner-notes.service';
import { SiteAreasService } from './site-areas.service';
import { SitesService } from './sites.service';
import { UsersService } from './users.service';
import { WorkerTicketsService } from './worker-tickets.service';

@Injectable({
  providedIn: 'root',
})
export class PlusService {
  private clientsService = inject(ClientsService);
  private initializeService = inject(InitializeService);
  private modalCtrl = inject(ModalController);
  private newsService = inject(NewsService);
  private partnerNotesService = inject(PartnerNotesService);
  private router = inject(Router);
  private sitesService = inject(SitesService);
  private siteAreasService = inject(SiteAreasService);
  private toastCtrl = inject(ToastController);
  private translate = inject(TranslateService);
  private usersService = inject(UsersService);
  private workerTicketsService = inject(WorkerTicketsService);

  isLoading = signal(false);

  async inviteUser(navigate = true, role?: Roles, sites?: string[]) {
    const m = await this.modalCtrl.create({ component: InviteUserDialogComponent, componentProps: { role } });
    m.present();

    const data = await m.onDidDismiss<User>();
    if (data.data) {
      const user = data.data;
      if (sites) {
        user.worksites = sites;
      }
      this.isLoading.set(true);
      const value = await firstValueFrom(this.usersService.invite(user));
      this.isLoading.set(false);
      if (value.result) {
        user.guid = value.user;
        this.initializeService.runUpdateUsers(user);
        if (navigate) {
          this.router.navigate(['/users', 'user', value.user]);
        }
        return { guid: user.guid, name: user.displayName, displayName: user.displayName, role: user.role };
      } else {
        Dialog.alert({
          title: this.translate.instant('users.creation'),
          message: this.translate.instant('users.creationFailed'),
        });
        return null;
      }
    } else {
      return null;
    }
  }

  async onAdHoc(sites: Site[], skipToast = false) {
    if (sites.length > 1 || sites.length === 0) {
      return this.workerTicketsService.openSiteCalendarDialog({ site: null, sites, skipToast });
    } else if (sites.length === 1) {
      const site = sites[0];
      return this.workerTicketsService.openSiteCalendarDialog({ site, sites, skipToast });
    }
  }

  onNewReport(site: Site) {
    this.modalCtrl.create({
      component: NewSiteReportDialogComponent,
      componentProps: { site },
      cssClass: ['medium-modal', 'medium-100'],
      backdropDismiss: false,
    }).then((m) => {
      m.present();
    });
  }

  onInstruction(type: 'general' | 'residence') {
    this.modalCtrl.create({ component: NewItemDialogComponent, componentProps: { type } }).then((m) => {
      m.present();
    });
  }

  onNewMessage() {
    this.router.navigate(['/worker', 'messages', 'new']);
  }

  onNews(segment?: string) {
    if (segment === 'feedbacks') {
      this.modalCtrl.create({ component: NewMessageDialogComponent }).then((m) => {
        m.present();
      });
    } else {
      this.modalCtrl.create({ component: NewNewsDialogComponent }).then((m) => {
        m.present();

        m.onDidDismiss<News>().then((data) => {
          if (data.data) {
            this.newsService.save(data.data).then(() => {
              this.toastCtrl.create({ message: this.translate.instant('messages.messageSent'), duration: 3000 }).then(async (t) => t.present());
            });
          }
        });
      });
    }
  }

  async onNewSite(clientGuid: string) {
    if (clientGuid == null) {
      clientGuid = this.clientsService.clientS()?.guid ?? null;
    }
    return this.modalCtrl
      .create({ component: NewSiteDialogComponent, componentProps: { clientGuid } })
      .then(async (m) => {
        m.present();
        return m.onDidDismiss().then((data) => {
          if (data.data) {
            const site = data.data;
            site.client = clientGuid;
            delete site.file;
            delete site.siteImageOptions;
            this.sitesService.save(site).then((docRef) => {
              site.guid = docRef.id;
            });
          }
        });
      });
  }

  private deleteChild(item: SiteArea, id: string) {
    if (item.children.length === 0) {
      return item.id !== id ? item : null;
    }

    item.children = item.children.map((c) => this.deleteChild(c, id)).filter((c) => c != null);
    return item;
  }

  onAddAreas(site: string, type: 'common' | 'residence' | 'commercial', floor?: string) {
    this.modalCtrl.create({
      component: AddAreaDialogComponent,
      componentProps: { type, floor },
      backdropDismiss: false,
      cssClass: ['stack-modal'],
    }).then((m) => {
      m.present();

      m.onDidDismiss<{
        areas?: { name: string; type: 'common' | 'residence' | 'commercial'; floor?: string }[]; rooms?: Room[]; delete?: boolean; name?: string;
      }>().then((data) => {
        if (data.data) {
          if (data.data.areas) {
            data.data.areas.forEach((child) => {
              const id = '0';
              const area: SiteArea = {
                id,
                name: child.name,
                site,
                children: [],
                rooms: [],
                type: child.type,
                floor: child.floor ?? null,
              };
              this.siteAreasService.save(area);
            });
          }
        }
      });
    });
  }

  onNewPartnerNote(status?: string) {
    this.modalCtrl
      .create({
        component: PartnerNoteDialogComponent, componentProps: { status }, cssClass: ['modal-fullscreen'],
      })
      .then((m) => {
        m.present();
        m.onDidDismiss<PartnerNote>().then((data) => {
          if (data.data) {
            data.data.inviter = this.usersService.currentUserS().guid;
            this.partnerNotesService.save(data.data);
          }
        });
      });
  }
}
